import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { runtimeConfig } from "../../app/runtime";
import { partnerApi } from "../shared/api";
import { partnerReturnNavigation } from "./browser-return";

/** Read-only readiness polling, independent of mutation retry/focus suspension. */
export function usePartnerReturn(stage: "payment" | "completed" | "canceled", sessionToken?: string) {
  const [state, setState] = useState<{ isPending: boolean; error?: Error }>({ isPending: false });
  const retry = useRef(() => {});
  const mutate = useCallback(() => retry.current(), []);

  useEffect(() => {
    setState({ isPending: false });
    if (stage === "payment" || !sessionToken) { retry.current = () => {}; return; }
    let disposed = false;
    let navigated = false;
    let inFlight = false;
    let stopped = false;
    let resumeRequested = false;
    let timer: number | undefined;
    const eligibleAt = Date.now() + (stage === "completed" ? 3000 : 0);
    const clearTimer = () => { window.clearTimeout(timer); timer = undefined; };
    const schedule = (delay: number) => { clearTimer(); timer = window.setTimeout(() => { void check(); }, delay); };
    const check = async () => {
      if (disposed || navigated || stopped) return;
      if (Date.now() < eligibleAt) { schedule(eligibleAt - Date.now()); return; }
      if (inFlight) { resumeRequested = true; return; }
      clearTimer();
      setState({ isPending: true });
      // A frozen/background tab may not run timers. Visibility/pageshow/online wakes it immediately.
      if (document.visibilityState === "hidden" || !navigator.onLine) return;
      inFlight = true;
      try {
        const result = await partnerApi.prepareReturn(sessionToken);
        if (disposed) return;
        if (result.callbackRequired !== false && result.callbackDelivered !== true) {
          throw new ApiError({ kind: "conflict", code: "PARTNER_RETURN_CONFIRMATION_UNAVAILABLE", message: "가입 결과 전달을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요." });
        }
        navigated = true;
        const base = runtimeConfig.apiBaseUrl.replace(/\/api$/, "");
        partnerReturnNavigation.assign(`${base}/api/v1/partner/join/${sessionToken}/return`);
      } catch (cause) {
        if (disposed) return;
        if (cause instanceof ApiError && cause.code === "PARTNER_CALLBACK_PENDING") {
          schedule(resumeRequested ? 0 : 5000);
        } else {
          stopped = true;
          setState({ isPending: false, error: cause instanceof Error ? cause : new Error("복귀하지 못했습니다. 잠시 후 다시 시도해 주세요.") });
        }
      } finally {
        inFlight = false;
        resumeRequested = false;
      }
    };
    const resume = () => { if (document.visibilityState !== "hidden") void check(); };
    retry.current = () => { stopped = false; void check(); };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", resume);
    schedule(Math.max(0, eligibleAt - Date.now()));
    return () => {
      disposed = true;
      clearTimer();
      retry.current = () => {};
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
    };
  }, [stage, sessionToken]);

  return { isPending: state.isPending, isError: Boolean(state.error), error: state.error, mutate };
}
